import { ReactNode, useEffect, useRef, useState } from 'react';

/**
 * A colored, labeled tag in the game header, with its explanation behind a tap.
 *
 * Both halves used to be wrong on a phone. A 9px square says nothing without its tooltip, and a
 * `title` tooltip is a desktop-only affordance — on touch it simply never appears, so the detail
 * was unreachable for anyone on a phone. So: the word is on the tag itself, and the detail is a
 * real panel toggled by pressing it, which behaves identically with a mouse. Same pattern as the
 * profile page's `?` cards.
 *
 * `tone` names a CSS class that sets `--diff-color`; the tag, its hover state and the panel's
 * heading are all tinted from that one variable.
 */
export function HeaderTag({ label, tone, ariaLabel, children }: {
  label: string;
  tone: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // `pointerdown` rather than `click`, so a tap aimed elsewhere closes the panel and still
    // reaches what it was aimed at.
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className="diff-tag-wrap" ref={ref}>
      <button
        type="button"
        className={`diff-tag ${tone}${open ? ' diff-tag-on' : ''}`}
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
      >
        {label}
      </button>
      {open && <span className="diff-pop">{children}</span>}
    </span>
  );
}
